import { useEffect, useState } from 'preact/hooks';
import type { Product } from '../../../models/Product';
import type { ProductGroup } from '../../../models/ProductGroup';
import Icon from '../../icon/Icon';
import './ProductPackSelector.less';
import ProductPackSelectorItem from './ProductPackSelectorItem';
import Button from '../../button/Button';
import { formatCurrency } from '../../../services/FormatterService';
import { useTranslation } from '../../../hooks/useTranslation';

export type ProductPackSelectorProps = {
    fixedGroup: ProductGroup;
    alternativesGroups: ProductGroup[];
    onAddToCart: (products: Product[]) => void;
    source: 'brilliant' | 'web';
};

const ProductPackSelector = ({
    fixedGroup,
    alternativesGroups,
    onAddToCart,
    source
}: ProductPackSelectorProps) => {
    const { t } = useTranslation();
    const fixedProduct = fixedGroup.products[0];
    const [visibleProductIndexes, setVisibleProductIndexes] = useState<
        number[]
    >([]);
    const [selectedSlots, setSelectedSlots] = useState<number[]>([]);
    useEffect(() => {
        setVisibleProductIndexes(alternativesGroups.map(() => 0));
        setSelectedSlots(alternativesGroups.map((_, index) => index));
    }, [alternativesGroups]);
    const selectedProducts = [
        fixedProduct,
        ...selectedSlots.map(
            slot =>
                alternativesGroups[slot].products[visibleProductIndexes[slot]]
        )
    ];
    const totalPrice = selectedProducts.reduce(
        (total, product) => total + product.unitPrice,
        0
    );
    if (alternativesGroups.length !== visibleProductIndexes.length) {
        return null;
    }
    return (
        <div className={`bri-ProductPackSelector`}>
            <div className="bri-ProductPackSelector-fixed-group">
                <ProductPackSelectorItem
                    product={fixedProduct}
                    footerContent={t('PRODUCT_PACK_BANNER_CURRENT_PRODUCT')}
                    source={source}
                />
            </div>
            <div className="bri-ProductPackSelector-plus">
                <Icon iconId="plus" size="medium" />
            </div>
            {alternativesGroups.map((group, index) => (
                <div className="bri-ProductPackSelector-alternative">
                    <ProductPackSelectorItem
                        animationDelay={(index + 1) * 100}
                        key={group.title + index}
                        product={group.products[visibleProductIndexes[index]]}
                        footerContent={group.title}
                        hasMore={group.products.length > 1}
                        showPagination={group.products.length > 1}
                        isSelectable
                        isSelected={selectedSlots.includes(index)}
                        source={source}
                        onChangeSelection={() => {
                            setSelectedSlots(
                                selectedSlots.includes(index)
                                    ? selectedSlots.filter(
                                          slot => slot !== index
                                      )
                                    : [...selectedSlots, index]
                            );
                        }}
                        onNext={() => {
                            const currentIndex = visibleProductIndexes[index];
                            const nextIndex =
                                (currentIndex + 1) % group.products.length;
                            const newProducts = [...visibleProductIndexes];
                            newProducts[index] = nextIndex;
                            setVisibleProductIndexes(newProducts);
                        }}
                        onPrev={() => {
                            const currentIndex = visibleProductIndexes[index];
                            const nextIndex =
                                (currentIndex - 1 + group.products.length) %
                                group.products.length;
                            const newProducts = [...visibleProductIndexes];
                            newProducts[index] = nextIndex;
                            setVisibleProductIndexes(newProducts);
                        }}
                    />
                </div>
            ))}
            <div className="bri-ProductPackSelector-result">
                <div className="bri-DisplayBoldFont bri-ProductPackSelector-result-title">
                    {t('PRODUCT_PACK_BANNER_TITLE')}
                </div>
                <div className="bri-Body2Font">
                    {t('PRODUCT_PACK_BANNER_SUBTITLE')}
                </div>
                <div className="bri-ProductPackSelector-total">
                    <div className="bri-Body2BoldFont">{t('TOTAL')}</div>
                    <div className="bri-TitleBoldFont bri-ProductPackSelector-total-price">
                        {formatCurrency(
                            totalPrice,
                            fixedProduct.currency,
                            fixedProduct.locale
                        )}
                    </div>
                </div>
                <Button
                    className="bri-ProductPackSelector-add-to-cart"
                    variant="magic"
                    onClick={() => onAddToCart(selectedProducts)}
                    fullWidth
                    size="large"
                >
                    {t('ADD_TO_CART_BUTTON_LABEL_MANY', {
                        count: selectedProducts.length
                    })}
                </Button>
            </div>
        </div>
    );
};

export default ProductPackSelector;
